import styled, { css } from 'styled-components'

export interface MapStyledProps {
  $width?: string | number
  $height?: string | number
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !prop.startsWith('$') &&
    !['setclickfeature', 'setdimension', 'loaded'].includes(prop),
})<MapStyledProps>`
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height || '600px'};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};

  .mapboxgl-map {
    width: 100%;
    height: 100%;
  }

  /* Style popups */
  .mapboxgl-popup-content {
    padding: 0 !important;
  }

  /* Hide default Mapbox controls */
  .mapboxgl-ctrl-top-left,
  .mapboxgl-ctrl-top-right,
  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right {
    display: none !important;
  }

  .mapboxgl-ctrl.mapboxgl-ctrl-group {
    display: none !important;
  }

  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`

export const MapSearchContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  width: 100%;
  max-width: 400px;
  padding: 0 16px;

  @media (max-width: 768px) {
    top: 12px;
    max-width: calc(100% - 24px);
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    top: 8px;
    max-width: calc(100% - 16px);
    padding: 0 8px;
  }
`

export const ResizeHandle = styled.div`
  position: absolute;
  left: -4px;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  background: transparent;
  transition: all 0.2s ease;
  z-index: 1001;

  /* Visual indicator bar */
  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 40px;
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.15;

    &::before {
      opacity: 0.6;
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary};
    opacity: 0.25;

    &::before {
      opacity: 1;
      background: ${({ theme }) => theme.colors.primary};
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`

export const MapControls = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 5;

  @media (max-width: 768px) {
    top: 80px;
    right: 8px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    top: 70px;
    right: 8px;
    gap: 4px;
  }
`

export const LocationFAB = styled.button`
  position: absolute;
  bottom: 80px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;
  margin-bottom: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &[data-tooltip]:hover::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 12px);
    right: 50%;
    transform: translateX(50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    animation: fadeIn 0.3s ease-in-out forwards;
    animation-delay: 0.5s;
  }

  &[data-tooltip]:hover::after {
    content: '';
    position: absolute;
    bottom: calc(100% + 6px);
    right: 50%;
    transform: translateX(50%);
    border: 6px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.8);
    opacity: 0;
    animation: fadeIn 0.3s ease-in-out forwards;
    animation-delay: 0.5s;
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    bottom: 80px;
    right: 8px;
    width: 40px;
    height: 40px;
  }

  @media (max-width: 480px) {
    bottom: 80px;
    right: 8px;
    width: 36px;
    height: 36px;
  }
`

export const ControlGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $horizontal?: boolean }>`
  /* background: ${({ theme }) => theme.colors.background.primary}; */
  /* border-radius: 12px; */
  /* box-shadow: ${({ theme }) => theme.shadows.md}; */
  overflow: hidden;
  display: flex;
  flex-direction: ${({ $horizontal }) => ($horizontal ? 'row' : 'column')};

  .map-control {
    border-radius: 0;
    box-shadow: none;
    border-bottom: ${({ $horizontal }) =>
      $horizontal ? 'none' : '1px solid rgba(0,0,0,0.06)'};
    border-right: ${({ $horizontal }) =>
      $horizontal ? '1px solid rgba(0,0,0,0.06)' : 'none'};

    &:last-child {
      border: none;
    }

    &:first-child {
      border-top-left-radius: 12px;
      border-top-right-radius: ${({ $horizontal }) =>
        $horizontal ? '0' : '12px'};
      border-bottom-left-radius: ${({ $horizontal }) =>
        $horizontal ? '12px' : '0'};
    }

    &:last-child {
      border-top-right-radius: ${({ $horizontal }) =>
        $horizontal ? '12px' : '0'};
      border-bottom-left-radius: ${({ $horizontal }) =>
        $horizontal ? '0' : '12px'};
      border-bottom-right-radius: 12px;
    }
  }
`

export const ZoomControls = styled.div`
  position: absolute;
  bottom: 180px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 5;

  @media (max-width: 768px) {
    bottom: 180px;
    right: 8px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    bottom: 170px;
    right: 8px;
    gap: 4px;
  }
`

export const MapFAB = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 24px;
  right: 16px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 173, 230, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 173, 230, 0.4);
  }

  &:active {
    transform: scale(0.95)
      ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};
  }

  .icon {
    width: 24px;
    height: 24px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  @media (max-width: 768px) {
    bottom: 24px;
    right: 8px;
  }

  @media (max-width: 480px) {
    bottom: 20px;
    right: 8px;
    width: 36px;
    height: 36px;
  }
`

export const DrawTools = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 140px;
  right: 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 8px;
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  transform-origin: bottom right;
  animation: ${({ $active }) =>
    $active ? 'popIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};
  z-index: 2;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 768px) {
    bottom: 140px;
    right: 8px;
    min-width: 160px;
    max-width: calc(100vw - 32px);
  }

  @media (max-width: 480px) {
    bottom: 130px;
    right: 8px;
    min-width: 140px;
    max-width: calc(100vw - 24px);
    padding: 6px;
  }
`

export const DrawToolButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean; $variant?: 'default' | 'delete' }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ $variant, theme }) =>
    $variant === 'delete' ? theme.colors.danger : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};

  &:hover {
    background: ${({ $variant, theme }) =>
      $variant === 'delete'
        ? 'rgba(255,59,48,0.1)'
        : theme.colors.background.tertiary};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    `}

  .icon {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    flex-shrink: 0;
  }
`

export const DrawToolsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 4px 8px;
`

export const LayersPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  top: 0;
  right: -280px;
  width: 280px;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-radius: 8px 0 0 8px;
  transform: ${({ $active }) =>
    $active ? 'translateX(-280px)' : 'translateX(0)'};

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    max-width: 320px;
    right: calc((100vw - 32px) * -1);
    transform: ${({ $active }) =>
      $active
        ? `translateX(calc((100vw - 32px) * -1 + 16px))`
        : 'translateX(0)'};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`

export const LayersHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const LayersTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const LayersClose = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .icon {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }
`

export const LayersContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`

export const LayerGroup = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const LayerGroupTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 12px;
  padding: 0 4px;
`

export const LayerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const LayerLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
`

export const LegendPanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  position: absolute;
  bottom: 24px;
  right: 16px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 16px;
  min-width: 200px;
  max-width: 280px;
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  z-index: 5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${({ $active }) => ($active ? 'fadeInUp 0.3s ease' : 'none')};

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    bottom: 24px;
    right: 8px;
    max-width: 240px;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 8px;
    max-width: 200px;
    padding: 12px;
  }
`

export const LegendHeader = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const LegendContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const LegendGradient = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $gradient?: string }>`
  width: 100%;
  height: 24px;
  border-radius: 4px;
  margin-bottom: 8px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: ${({ $gradient }) =>
    $gradient || 'linear-gradient(to right, #e0e0e0, #bdbdbd)'};
`

export const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 4px;
`

export const LegendUnit = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-align: center;
  margin-top: 4px;
`

export const HoverBox = styled.div<{ left?: number; top?: number }>`
  position: absolute;
  left: ${({ left }) => (left ? `${left}px` : '0')};
  top: ${({ top }) => (top ? `${top}px` : '0')};
  transform: translate(-50%, -100%);
  margin-top: -10px;
  pointer-events: none;
  z-index: 100;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 200px;
  font-size: 14px;
`

export const FullscreenButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $isFullscreen?: boolean }>`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1002;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  .icon {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  @media (max-width: 768px) {
    top: 12px;
    left: 12px;
    width: 36px;
    height: 36px;

    .icon {
      width: 16px;
      height: 16px;
    }
  }
`
